import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import ScorecardNavigation from './ScorecardNavigation';
import PinnedHeaderTable from './stickyTableHeader/PinnedHeaderTable';
import MonthFilter from './MonthFilter';
import ErrorMessage from '../common/ErrorMessage';
import LoadingSpinner from '../common/LoadingSpinner';
import {
  leaderboardData,
  getAvailableMonths,
  getLatestMonth,
  filterDataByMonth,
  type LeaderboardEntry,
  type StoreScorecardEntry
} from '../../data/scorecardData';
import managementService from '../../services/managementService';
import { getServerStatsRevelOrderCounts } from '../../services/scopedReadsService';
import { config } from '../../config/environment';
import { computeScore } from '../../utils/scoreUtils';
import * as Sentry from '@sentry/react';

const Leaderboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedTenant } = useTenant();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    if (config.useDummyData) {
      return getLatestMonth(leaderboardData);
    }
    return ''; // For real data, we'll set this after data is loaded
  });
  
  const [leaderboardEntries, setLeaderboardEntries] = useState<LeaderboardEntry[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Raw store entries for Revel score recalculation
  const [rawStoreEntries, setRawStoreEntries] = useState<{ locationName: string; entries: StoreScorecardEntry[] }[]>([]);
  const [allServerShifts, setAllServerShifts] = useState<any[]>([]);
  const [revelOrderCounts, setRevelOrderCounts] = useState<Record<string, number>>({});
  const [isRecalculatingScores, setIsRecalculatingScores] = useState(false);

  // Normalize role to handle case-insensitive comparison
  const normalizedRole = useMemo(() => {
    const rawRole = user?.managementUser?.role;
    if (!rawRole) return null;
    return rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase();
  }, [user?.managementUser?.role]);

  // Fetch locations for store names
  useEffect(() => {
    const fetchLocations = async () => {
      if (config.useDummyData) return;
      
      console.log('Leaderboard - fetchLocations called', { normalizedRole, selectedTenant });
      
      // Clear locations when tenant changes
      setLocations([]);
      
      // Don't fetch locations if admin user hasn't selected a tenant
      const isAdminWithoutTenant = normalizedRole === 'Admin' && !selectedTenant;
      if (isAdminWithoutTenant) {
        console.log('Leaderboard - Admin without tenant, skipping location fetch');
        return;
      }
      
      try {
        const response = await managementService.getLocationsByTenant(selectedTenant || undefined);
        console.log('Leaderboard - Locations response:', {
          success: response.success,
          locationsCount: response.locations?.length || 0,
          error: response.error
        });
        
        if (response.success && response.locations) {
          setLocations(response.locations);
        } else {
          // Only set error if it's not the "tenant required" error for admin users
          if (response.error !== 'Tenant ID is required to fetch locations' || 
              normalizedRole !== 'Admin') {
            console.error('Leaderboard - Error fetching locations:', response.error);
            setError(response.error || 'Failed to fetch locations');
          }
        }
      } catch (err) {
        console.error('Leaderboard - Exception fetching locations:', err);
        Sentry.captureException(err);
        setError('Failed to fetch locations');
      }
    };

    fetchLocations();
  }, [selectedTenant, user, normalizedRole]);

  // Fetch store scores for all locations
  useEffect(() => {
    const fetchStoreScores = async () => {
      if (config.useDummyData) {
        setIsLoading(false);
        return;
      }
      
      console.log('Leaderboard - fetchStoreScores called', { 
        locationsCount: locations.length, 
        normalizedRole, 
        selectedTenant 
      });
      
      if (locations.length === 0) {
        // No locations available, stop loading
        console.log('Leaderboard - No locations available, stopping fetch');
        setIsLoading(false);
        return;
      }
      
      // Clear previous data when starting new fetch
      setLeaderboardEntries([]);
      setIsLoading(true);
      setError(null);
      
      try {
        const allRawData: { locationName: string; entries: StoreScorecardEntry[] }[] = [];

        // Fetch scores for each location (all available data, no date filtering)
        const tenantId = normalizedRole === 'Admin' ? (selectedTenant || undefined) : undefined;

        for (const location of locations) {
          try {
            const response = await managementService.getStoreScorecardData(location.id, undefined, undefined, tenantId);

            if (response.success && response.entries) {
              allRawData.push({ locationName: location.name, entries: response.entries });
            } else if (response.error) {
              console.warn(`Leaderboard - Error response for ${location.name}:`, response.error);
            }
          } catch (err) {
            console.error(`Leaderboard - Error fetching scores for ${location.name}:`, err);
            Sentry.captureException(err);
          }
        }

        setRawStoreEntries(allRawData);
      } catch (err) {
        console.error('Leaderboard - Error in fetchStoreScores:', err);
        Sentry.captureException(err);
        setError('Failed to fetch store scores');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoreScores();
  }, [locations, normalizedRole, selectedTenant]);

  // Fetch server-level data for all managers (for Revel greetings recalculation)
  useEffect(() => {
    const fetchServerLevelData = async () => {
      if (config.useDummyData || rawStoreEntries.length === 0) {
        setAllServerShifts([]);
        setIsRecalculatingScores(false);
        return;
      }

      setIsRecalculatingScores(true);
      const managerIdSet = new Set<string>();
      rawStoreEntries.forEach(({ entries }) => {
        entries.forEach(e => managerIdSet.add(e.manager_id));
      });
      const allManagerIds = Array.from(managerIdSet);

      const allShifts: any[] = [];
      for (const managerId of allManagerIds) {
        try {
          const response = await managementService.getShiftsByManagerId(managerId);
          if (response.success && response.shifts) {
            allShifts.push(...response.shifts);
          }
        } catch (err) {
          console.warn('Leaderboard - Failed to fetch shifts for manager:', managerId, err);
          Sentry.captureException(err);
        }
      }
      setAllServerShifts(allShifts);
    };

    fetchServerLevelData();
  }, [rawStoreEntries]);

  // Fetch Revel order counts for all server shifts
  useEffect(() => {
    const fetchRevelOrderCounts = async () => {
      if (config.useDummyData || allServerShifts.length === 0) {
        setRevelOrderCounts({});
        setIsRecalculatingScores(false);
        return;
      }

      const shiftIds = allServerShifts
        .map(s => s.server_shift_id)
        .filter((id): id is string => !!id);

      if (shiftIds.length === 0) {
        setRevelOrderCounts({});
        setIsRecalculatingScores(false);
        return;
      }

      // feat-133 Batch-6: revel order counts via the tenant-scoped Batch-3
      // endpoint (byte-identical query; foreign shift_ids contribute 0 rows).
      // Tenant from auth context; without one there is nothing to scope (the
      // admin-without-tenant case is already gated above).
      const tenantId = selectedTenant || user?.managementUser?.tenant_id || undefined;
      if (!tenantId) {
        setRevelOrderCounts({});
        setIsRecalculatingScores(false);
        return;
      }

      try {
        const BATCH_SIZE = 100;
        const counts: Record<string, number> = {};
        for (let i = 0; i < shiftIds.length; i += BATCH_SIZE) {
          const batchShiftIds = shiftIds.slice(i, i + BATCH_SIZE);
          const rows = await getServerStatsRevelOrderCounts(tenantId, batchShiftIds);
          for (const row of rows) {
            counts[row.shift_id] = parseInt(row.order_count, 10) || 0;
          }
        }
        setRevelOrderCounts(counts);
      } catch (err) {
        console.warn('Leaderboard - Revel order count fetch failed:', err);
        setRevelOrderCounts({});
      } finally {
        setIsRecalculatingScores(false);
      }
    };

    fetchRevelOrderCounts();
  }, [allServerShifts]);

  // Recalculate manager shift scores using Revel-adjusted greetings
  const recalculatedScores = useMemo(() => {
    if (allServerShifts.length === 0) return {};

    const byManagerShift: Record<string, any[]> = {};
    for (const shift of allServerShifts) {
      const managerShiftId = shift.id;
      if (!byManagerShift[managerShiftId]) byManagerShift[managerShiftId] = [];
      byManagerShift[managerShiftId].push(shift);
    }

    const scores: Record<string, number> = {};
    for (const [managerShiftId, serverShifts] of Object.entries(byManagerShift)) {
      let totalScore = 0;
      for (const shift of serverShifts) {
        const upsells = shift.upsell_count ?? 0;
        const mentions = shift.mention_count ?? 0;
        const describes = shift.describe_count ?? 0;
        const drinkPromos = shift.drink_promotion_count ?? 0;
        const revelCount = revelOrderCounts[shift.server_shift_id || ''] || 0;
        const baseGreetings = revelCount > 0 ? revelCount : (shift.greetings ?? 0);
        const maxUpsells = Math.max(upsells, mentions, describes, drinkPromos);
        const greetings = Math.max(baseGreetings, maxUpsells);
        const pm = greetings > 0 ? Math.round((upsells / greetings) * 100) : 0;
        const im = greetings > 0 ? Math.round((mentions / greetings) * 100) : 0;
        const id = greetings > 0 ? Math.round((describes / greetings) * 100) : 0;
        const dp = greetings > 0 ? Math.round((drinkPromos / greetings) * 100) : 0;
        const score = computeScore([pm, im, id], dp);
        totalScore += score;
      }
      scores[managerShiftId] = Math.round(totalScore / serverShifts.length);
    }

    return scores;
  }, [allServerShifts, revelOrderCounts]);

  // Build leaderboard entries from raw data with recalculated scores
  useEffect(() => {
    if (rawStoreEntries.length === 0) {
      setLeaderboardEntries([]);
      return;
    }

    const allStoreScores: LeaderboardEntry[] = [];
    for (const { locationName, entries } of rawStoreEntries) {
      const monthlyScores = new Map<string, number[]>();
      entries.forEach(entry => {
        const date = new Date(entry.date);
        const monthKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        if (!monthlyScores.has(monthKey)) monthlyScores.set(monthKey, []);
        const score = recalculatedScores[entry.shift_id || ''] ?? entry.score;
        monthlyScores.get(monthKey)!.push(score);
      });

      monthlyScores.forEach((scores, monthKey) => {
        const avg = scores.reduce((sum, s) => sum + s, 0) / scores.length;
        allStoreScores.push({
          date: monthKey,
          store: locationName,
          score: Math.round(avg),
          previousRank: 0,
          currentRank: 0,
          change: 0
        });
      });
    }

    setLeaderboardEntries(allStoreScores);

    // Set selected month if not already set
    if (!selectedMonth && allStoreScores.length > 0) {
      const uniqueMonths = Array.from(new Set(allStoreScores.map(e => e.date)))
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
      if (uniqueMonths.length > 0) {
        setSelectedMonth(uniqueMonths[0]);
      }
    }
  }, [rawStoreEntries, recalculatedScores]);

  // Get available months from data (only show months with entries)
  const availableMonths = useMemo(() => {
    if (config.useDummyData) {
      return getAvailableMonths(leaderboardData);
    }
    
    // For real data, extract months from leaderboardEntries
    if (leaderboardEntries.length === 0) {
      return [];
    }
    
    // Get unique months from entries (date field now contains month strings)
    const monthsWithData = new Set<string>();
    leaderboardEntries.forEach(entry => {
      monthsWithData.add(entry.date);
    });
    
    // Sort months in descending order (most recent first)
    return Array.from(monthsWithData).sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB.getTime() - dateA.getTime();
    });
  }, [leaderboardEntries]);

  // Filter data based on selected month and calculate rankings
  const filteredData = useMemo(() => {
    console.log('Leaderboard - filteredData calculation', {
      selectedMonth,
      leaderboardEntriesCount: leaderboardEntries.length,
      useDummyData: config.useDummyData
    });
    
    if (config.useDummyData) {
      let filtered = leaderboardData;
      
      if (selectedMonth !== 'All') {
        filtered = leaderboardData.filter(entry => {
          const date = new Date(entry.date);
          const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          return monthYear === selectedMonth;
        });
      }

      // Group by month and calculate rankings
      const monthlyData = new Map();
      filtered.forEach(entry => {
        const date = new Date(entry.date);
        const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        
        if (!monthlyData.has(monthYear)) {
          monthlyData.set(monthYear, []);
        }
        monthlyData.get(monthYear).push(entry);
      });

      // Get the most recent month's data
      const sortedMonths = Array.from(monthlyData.keys()).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
      const currentMonthData = monthlyData.get(sortedMonths[0]) || [];

      // Sort by score descending and assign ranks
      const rankedData = currentMonthData
        .sort((a: LeaderboardEntry, b: LeaderboardEntry) => b.score - a.score)
        .map((entry: LeaderboardEntry, index: number) => ({
          ...entry,
          currentRank: index + 1
        }));

      return rankedData;
    }
    
    // For real data, handle month filtering and "All" case
    if (leaderboardEntries.length === 0) {
      return [];
    }
    
    // If no month is selected yet (empty string), show all data or wait
    if (!selectedMonth || selectedMonth === '') {
      // Show all data when month hasn't been selected yet
      // This allows data to be visible while month detection is happening
      const storeOverallScores = new Map<string, { totalScore: number; count: number }>();
      
      leaderboardEntries.forEach(entry => {
        if (!storeOverallScores.has(entry.store)) {
          storeOverallScores.set(entry.store, { totalScore: 0, count: 0 });
        }
        const storeData = storeOverallScores.get(entry.store)!;
        storeData.totalScore += entry.score;
        storeData.count += 1;
      });
      
      // Create entries with overall averages
      const overallEntries = Array.from(storeOverallScores.entries()).map(([store, data]) => ({
        date: 'All Time',
        store,
        score: Math.round(data.totalScore / data.count),
        previousRank: 0,
        currentRank: 0,
        change: 0
      }));
      
      // Sort by score descending and assign ranks
      return overallEntries
        .sort((a: LeaderboardEntry, b: LeaderboardEntry) => b.score - a.score)
        .map((entry: LeaderboardEntry, index: number) => ({
          ...entry,
          currentRank: index + 1
        }));
    }
    
    if (selectedMonth === 'All') {
      // Calculate overall average scores across all months for each store
      const storeOverallScores = new Map<string, { totalScore: number; count: number }>();
      
      leaderboardEntries.forEach(entry => {
        if (!storeOverallScores.has(entry.store)) {
          storeOverallScores.set(entry.store, { totalScore: 0, count: 0 });
        }
        const storeData = storeOverallScores.get(entry.store)!;
        storeData.totalScore += entry.score;
        storeData.count += 1;
      });
      
      // Create entries with overall averages
      const overallEntries = Array.from(storeOverallScores.entries()).map(([store, data]) => ({
        date: 'All Time',
        store,
        score: Math.round(data.totalScore / data.count),
        previousRank: 0,
        currentRank: 0,
        change: 0
      }));
      
      // Sort by score descending and assign ranks
      return overallEntries
        .sort((a: LeaderboardEntry, b: LeaderboardEntry) => b.score - a.score)
        .map((entry: LeaderboardEntry, index: number) => ({
          ...entry,
          currentRank: index + 1
        }));
    }
    
    // Filter by specific month
    const filtered = leaderboardEntries.filter(entry => entry.date === selectedMonth);
    
    console.log('Leaderboard - Filtered by month', {
      selectedMonth,
      filteredCount: filtered.length,
      allEntries: leaderboardEntries.map(e => ({ store: e.store, date: e.date, score: e.score }))
    });

    // Sort by score descending and assign ranks
    const rankedData = filtered
      .sort((a: LeaderboardEntry, b: LeaderboardEntry) => b.score - a.score)
      .map((entry: LeaderboardEntry, index: number) => ({
        ...entry,
        currentRank: index + 1
      }));

    console.log('Leaderboard - Final ranked data', rankedData.length);
    return rankedData;
  }, [selectedMonth, leaderboardEntries]);

  // Calculate change vs last month for each store
  const dataWithChanges = useMemo(() => {
    if (selectedMonth === 'All') {
      // For "All" mode, no change calculation needed
      return filteredData.map((entry: LeaderboardEntry) => ({
        ...entry,
        change: null
      }));
    }
    
    return filteredData.map((entry: LeaderboardEntry) => {
      // Find the same store's rank in the previous month
      const currentDate = new Date(entry.date);
      const previousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 15);
      const previousMonthYear = previousMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      // Get all stores from the previous month and sort by score to get rankings
      const previousMonthData = leaderboardEntries
        .filter(data => data.date === previousMonthYear)
        .sort((a, b) => b.score - a.score);

      // Find the rank of the current store in the previous month
      const previousRank = previousMonthData.length > 0 ? 
        previousMonthData.findIndex(data => data.store === entry.store) + 1 : null;

      // Calculate change: positive means improved (moved up), negative means declined (moved down)
      const change = previousRank ? previousRank - entry.currentRank : null;

      return {
        ...entry,
        change: change ?? null
      };
    });
  }, [filteredData, leaderboardEntries, selectedMonth]);

  const getChangeColor = (change: number | null | undefined) => {
    if (change === null || change === undefined) return 'text-gray-500 bg-gray-100';
    if (change > 0) return 'text-green-600 bg-green-100';
    if (change < 0) return 'text-red-600 bg-red-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getChangeText = (change: number | null | undefined) => {
    if (change === null || change === undefined) return 'N/A';
    if (change > 0) return `↑ ${change}`;
    if (change < 0) return `↓ ${Math.abs(change)}`;
    return '0';
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 80) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const handleViewStore = (store: string) => {
    // Find the location ID for the selected store
    const selectedLocation = locations.find(loc => loc.name === store);
    const locationId = selectedLocation ? selectedLocation.id : null;
    
    navigate('/restaurant/store-scorecard', { 
      state: { 
        storeData: { 
          storeName: store,
          storeId: locationId
        } 
      } 
    });
  };

  if (isLoading && !config.useDummyData) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error && !config.useDummyData) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <ErrorMessage message={error} />
        </div>
      </div>
    );
  }

  // Check if admin user needs to select a tenant
  const isAdminWithoutTenant = normalizedRole === 'Admin' && !selectedTenant;

  if (isAdminWithoutTenant) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Tenant</h3>
              <p className="text-gray-600 mb-4">
                Please select a tenant from the dropdown in the navigation bar to view leaderboard data.
              </p>
              <div className="text-sm text-gray-500">
                Use the tenant selector in the top-left corner to choose which tenant's data to view.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state when no locations are available
  if (!config.useDummyData && !isLoading && locations.length === 0 && selectedTenant) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Locations Found</h3>
              <p className="text-gray-600 mb-4">
                This tenant doesn't have any store locations configured yet.
              </p>
              <div className="text-sm text-gray-500">
                Store locations need to be added before viewing leaderboard data.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-display font-semibold text-ink mb-2">Leaderboard</h1>
          <p className="text-lg text-gray-600">
            Store performance rankings by month
          </p>
        </div>

        {/* Month Filter */}
        <div className="mb-6 flex justify-start">
          <MonthFilter
            availableMonths={availableMonths}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
          />
        </div>

        {/* Leaderboard Table */}
        {/* feat-297: overflow-clip (not -hidden) so the pinned header's sticky reaches the page. */}
        <div className="bg-white shadow overflow-clip sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Store Rankings</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Performance rankings for all store locations
            </p>
          </div>
          
          {/* feat-297: header pins beneath the nav. The thead/tbody line used to
              come from `divide-y divide-gray-200` on the table; the header now
              renders in its own strip, so that line is the strip's
              `border-b border-gray-200` instead (design NOTES §4). */}
          <PinnedHeaderTable
            scrollerClassName="overflow-x-auto"
            tableClassName="min-w-full"
            stripClassName="border-b border-gray-200"
            thead={
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Store
                  </th>
                  {selectedMonth !== 'All' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Change (vs. Last Month)
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Store
                  </th>
                </tr>
              </thead>
            }
          >
              <tbody className="bg-white divide-y divide-gray-200">
                {dataWithChanges.length > 0 ? (
                  dataWithChanges.map((entry: LeaderboardEntry, index: number) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          <span className="text-lg font-bold text-indigo-600">
                            #{entry.currentRank}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium">{entry.store}</div>
                      </td>
                      {selectedMonth !== 'All' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {isRecalculatingScores && !config.useDummyData ? (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-400">...</span>
                          ) : (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getChangeColor(entry.change)}`}>
                              {getChangeText(entry.change)}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {isRecalculatingScores && !config.useDummyData ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-400">...</span>
                        ) : (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreColor(entry.score)}`}>
                            {entry.score}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button
                          onClick={() => handleViewStore(entry.store)}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                        >
                          View Store
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={selectedMonth === 'All' ? 4 : 5} className="px-6 py-4 text-center text-sm text-gray-500">
                      No leaderboard data available for the selected month.
                    </td>
                  </tr>
                )}
              </tbody>
          </PinnedHeaderTable>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
